import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, Calendar, TrendingUp, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

import { stripeService } from '../../../services/stripeService';
import Button from '../../../components/ui/Button';

export default function BillingIntegrationSection({ onStatsUpdate }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  useEffect(() => {
    loadBillingData();
    checkStripeStatus();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      
      // Load subscription data
      const { data, error } = await supabase
        ?.from('tenant_subscriptions')
        ?.select(`
          *,
          tier:subscription_tiers(*)
        `)
        ?.eq('tenant_id', profile?.tenant_id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (data) {
        setSubscription({
          id: data?.id,
          status: data?.status,
          billingCycle: data?.billing_cycle,
          currentPeriodStart: data?.current_period_start,
          currentPeriodEnd: data?.current_period_end,
          nextBillingDate: data?.next_billing_date,
          tier: {
            name: data?.tier?.name,
            basePrice: data?.tier?.base_price,
            perUserPrice: data?.tier?.per_user_price,
            features: data?.tier?.features || [],
            limits: data?.tier?.limits || {}
          }
        });

        onStatsUpdate?.({ activeSubscription: data?.tier?.name });
      }

      // Mock payment history (replace with actual Stripe data)
      setPaymentHistory([
        {
          id: '1',
          date: new Date()?.toISOString(),
          amount: 99.00,
          status: 'paid',
          description: 'Monthly subscription'
        }
      ]);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStripeStatus = () => {
    const status = stripeService?.getStripeStatus();
    setStripeStatus(status);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      trial: { color: 'bg-blue-100 text-blue-800', label: 'Trial' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Billing & Subscription</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your subscription, payment methods, and billing history
          </p>
        </div>
      </div>

      {/* Stripe Status Alert */}
      {stripeStatus && !stripeStatus?.configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-yellow-800">Stripe Integration Required</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {stripeStatus?.message}. {stripeStatus?.action}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
          {subscription && getStatusBadge(subscription?.status)}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-gray-500">Loading subscription details...</span>
          </div>
        ) : subscription ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Plan</p>
                <p className="text-xl font-bold text-gray-900">{subscription?.tier?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Billing Cycle</p>
                <p className="text-xl font-bold text-gray-900 capitalize">{subscription?.billingCycle}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Period</p>
                <p className="text-sm text-gray-900">
                  {formatDate(subscription?.currentPeriodStart)} - {formatDate(subscription?.currentPeriodEnd)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Next Billing Date</p>
                <p className="text-sm text-gray-900">{formatDate(subscription?.nextBillingDate)}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Plan Features</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {subscription?.tier?.features?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 flex gap-3">
              <Button variant="outline">
                Change Plan
              </Button>
              <Button variant="outline">
                Update Payment Method
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 mb-4">No active subscription</p>
            <Button>Choose a Plan</Button>
          </div>
        )}
      </div>

      {/* Stripe Dashboard Link */}
      {stripeStatus?.configured && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800">Stripe Dashboard</h3>
              <p className="text-sm text-blue-700 mt-1">
                View detailed billing information, invoices, and payment history in your Stripe dashboard.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => window?.open('https://dashboard.stripe.com', '_blank')}
              >
                Open Stripe Dashboard
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paymentHistory?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No payment history available
                  </td>
                </tr>
              ) : (
                paymentHistory?.map((payment) => (
                  <tr key={payment?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment?.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment?.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payment?.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {payment?.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Usage Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Monthly Spend</p>
            <DollarSign className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(subscription?.tier?.basePrice || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">Per {subscription?.billingCycle || 'month'}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Next Payment</p>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-lg font-bold text-gray-900">{formatDate(subscription?.nextBillingDate)}</p>
          <p className="text-xs text-gray-500 mt-1">Auto-renewal enabled</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Savings</p>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-green-600">$0</p>
          <p className="text-xs text-gray-500 mt-1">This billing period</p>
        </div>
      </div>
    </div>
  );
}
